/* Storybook TCG storefront enhancements — dependency-free and shared globally. */
(() => {
  if (window.__storybookEnhancementsLoaded) return;
  window.__storybookEnhancementsLoaded = true;

  /* ------------------------------------------------------------------------
     Shared desktop parallax controller
     ------------------------------------------------------------------------ */
  const desktopMedia = window.matchMedia('(min-width: 700px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const allParallax = new Set();
  const intersectingParallax = new Set();
  let frame = 0;
  let scrollListening = false;

  const parallaxEnabled = () => desktopMedia.matches && !reducedMotion.matches;

  const setParallaxActivity = (element, active) => {
    element.classList.toggle('is-parallax-active', active);
  };

  const updateVisibleParallax = () => {
    frame = 0;
    if (!parallaxEnabled()) return;
    intersectingParallax.forEach((element) => element.updatePosition());
  };

  const scheduleParallax = () => {
    if (!frame) frame = window.requestAnimationFrame(updateVisibleParallax);
  };

  const syncScrollListener = () => {
    const shouldListen = parallaxEnabled() && intersectingParallax.size > 0;
    if (shouldListen === scrollListening) return;
    scrollListening = shouldListen;

    if (shouldListen) {
      window.addEventListener('scroll', scheduleParallax, { passive: true });
    } else {
      window.removeEventListener('scroll', scheduleParallax);
    }
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) intersectingParallax.add(entry.target);
          else intersectingParallax.delete(entry.target);

          setParallaxActivity(entry.target, entry.isIntersecting && parallaxEnabled());
          if (!entry.isIntersecting) entry.target.reset();
        });

        syncScrollListener();
        scheduleParallax();
      }, { rootMargin: '25% 0px' })
    : null;

  class StorybookParallax extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.media = this.querySelector('.storybook-parallax__media');
      if (!this.media) return;

      this.speed = Number(this.dataset.speed) || 0.2;
      this.maxOffset = Math.max(0, Number(this.dataset.maxOffset) || 0);
      this.scale = Math.max(1, Number(this.dataset.scale) || 1);
      allParallax.add(this);

      if (observer) {
        observer.observe(this);
      } else {
        intersectingParallax.add(this);
        setParallaxActivity(this, parallaxEnabled());
      }

      handleParallaxModeChange();
    }

    disconnectedCallback() {
      this._connected = false;
      allParallax.delete(this);
      intersectingParallax.delete(this);
      setParallaxActivity(this, false);
      if (observer) observer.unobserve(this);
      syncScrollListener();
    }

    reset() {
      if (this.media) this.media.style.removeProperty('transform');
    }

    updatePosition() {
      if (!this.media || !parallaxEnabled()) return;
      const bounds = this.getBoundingClientRect();
      const distanceFromViewportCenter = bounds.top + bounds.height / 2 - window.innerHeight / 2;
      const rawOffset = distanceFromViewportCenter * this.speed * -1;
      const offset = Math.max(-this.maxOffset, Math.min(this.maxOffset, rawOffset));
      this.media.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0) scale(${this.scale})`;
    }
  }

  const handleParallaxModeChange = () => {
    const enabled = parallaxEnabled();

    allParallax.forEach((element) => {
      const active = enabled && (!observer || intersectingParallax.has(element));
      setParallaxActivity(element, active);
      if (!enabled) element.reset();
    });

    syncScrollListener();
    scheduleParallax();
  };

  if (!customElements.get('storybook-parallax')) {
    customElements.define('storybook-parallax', StorybookParallax);
  }

  if (desktopMedia.addEventListener) {
    desktopMedia.addEventListener('change', handleParallaxModeChange);
    reducedMotion.addEventListener('change', handleParallaxModeChange);
  } else {
    desktopMedia.addListener(handleParallaxModeChange);
    reducedMotion.addListener(handleParallaxModeChange);
  }
  window.addEventListener('resize', scheduleParallax, { passive: true });

  /* ------------------------------------------------------------------------
     Store help widget — accessible disclosure behavior and clean teardown
     ------------------------------------------------------------------------ */
  class StorybookSupport extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.toggle = this.querySelector('[data-support-toggle]');
      this.closeButton = this.querySelector('[data-support-close]');
      this.panel = this.querySelector('.storybook-support__panel');
      this.answerButtons = [...this.querySelectorAll('[data-support-answer]')];
      this.answers = [...this.querySelectorAll('[data-support-response]')];
      if (!this.toggle || !this.closeButton || !this.panel) return;

      this._onToggle = () => this.setOpen(this.dataset.open !== 'true', true);
      this._onClose = () => this.setOpen(false, true);
      this._onOutsidePointer = (event) => {
        if (this.dataset.open === 'true' && !this.contains(event.target)) this.setOpen(false, false);
      };
      this._onKeydown = (event) => {
        if (event.key === 'Escape' && this.dataset.open === 'true') {
          event.preventDefault();
          this.setOpen(false, true);
        }
      };
      this._onAnswerClick = (event) => {
        const button = event.target.closest('[data-support-answer]');
        if (button && this.contains(button)) this.toggleAnswer(button);
      };

      this.toggle.addEventListener('click', this._onToggle);
      this.closeButton.addEventListener('click', this._onClose);
      this.addEventListener('click', this._onAnswerClick);
      document.addEventListener('pointerdown', this._onOutsidePointer, { passive: true });
      document.addEventListener('keydown', this._onKeydown);
      this.setOpen(false, false);
    }

    disconnectedCallback() {
      if (!this._initialized) return;
      this.toggle?.removeEventListener('click', this._onToggle);
      this.closeButton?.removeEventListener('click', this._onClose);
      this.removeEventListener('click', this._onAnswerClick);
      document.removeEventListener('pointerdown', this._onOutsidePointer);
      document.removeEventListener('keydown', this._onKeydown);
      this._initialized = false;
    }

    setOpen(open, moveFocus) {
      this.dataset.open = open ? 'true' : 'false';
      this.toggle.setAttribute('aria-expanded', String(open));
      this.toggle.setAttribute('aria-label', open ? 'Close help' : 'Open help');
      this.panel.setAttribute('aria-hidden', String(!open));

      if (moveFocus) {
        window.requestAnimationFrame(() => {
          if (open) (this.answerButtons[0] || this.closeButton).focus();
          else this.toggle.focus();
        });
      }
    }

    toggleAnswer(activeButton) {
      const key = activeButton.dataset.supportAnswer;
      const willOpen = activeButton.getAttribute('aria-expanded') !== 'true';

      this.answerButtons.forEach((button) => {
        button.setAttribute('aria-expanded', String(button === activeButton && willOpen));
      });
      this.answers.forEach((answer) => {
        answer.hidden = !(answer.dataset.supportResponse === key && willOpen);
      });
    }
  }

  if (!customElements.get('storybook-support')) {
    customElements.define('storybook-support', StorybookSupport);
  }

  /* ------------------------------------------------------------------------
     Free-shipping progress with explicit Canada / US thresholds
     ------------------------------------------------------------------------ */
  class StorybookFreeShippingBar extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.message = this.querySelector('[data-shipping-message]');
      this.fill = this.querySelector('[data-shipping-fill]');
      this.track = this.querySelector('[data-shipping-track]');
      this.threshold = Math.max(1, Number(this.dataset.threshold) || 1);
      this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'CAD';
      this.country = this.dataset.country || 'CA';
      this._abortController = new AbortController();
      this._onCartChange = (event) => this.renderFromCart(event.detail?.cart);
      document.addEventListener('cart:change', this._onCartChange, { signal: this._abortController.signal });
      this.fetchCart();
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._initialized = false;
    }

    async fetchCart() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
          signal: this._abortController.signal
        });
        if (!response.ok) return;
        this.renderFromCart(await response.json());
      } catch (error) {
        if (error?.name !== 'AbortError') console.debug('Storybook free-shipping bar could not refresh.');
      }
    }

    renderFromCart(cart) {
      if (!cart || !this.message || !this.fill || !this.track) return;

      const shippableTotal = Array.isArray(cart.items)
        ? cart.items
            .filter((item) => item.requires_shipping !== false)
            .reduce((sum, item) => sum + (Number(item.final_line_price) || 0), 0)
        : Number(cart.total_price) || 0;

      const remaining = Math.max(this.threshold - shippableTotal, 0);
      const progress = Math.min(shippableTotal / this.threshold, 1);
      const locale = this.country === 'US' ? 'en-US' : 'en-CA';
      const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: this.currency });

      this.message.replaceChildren();
      if (remaining > 0) {
        this.message.append("You're ");
        const amount = document.createElement('strong');
        amount.textContent = formatter.format(remaining / 100);
        this.message.append(amount, ' away from FREE shipping');
      } else {
        this.message.textContent = '✨ You’ve unlocked FREE shipping!';
      }

      const percent = Math.round(progress * 100);
      this.fill.style.width = `${percent}%`;
      this.track.setAttribute('aria-valuenow', String(percent));
      this.track.setAttribute('aria-valuetext', remaining > 0
        ? `${formatter.format(remaining / 100)} remaining for free shipping`
        : 'Free shipping unlocked');
    }
  }

  if (!customElements.get('storybook-free-shipping-bar')) {
    customElements.define('storybook-free-shipping-bar', StorybookFreeShippingBar);
  }
})();
